var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useRef, useEffect } from "react";
import { Handle, Position } from "reactflow";
import cc from "classcat";
import { NodeIcon } from "./NodeIcon";
import "./QueryNode.css";
import { useGraphRenderingStore } from "./store";
import { assert } from "../loader-utils";
function useResizeObservedRef(resizeObserver) {
    var ref = useRef(null);
    useEffect(function () {
        assert(ref.current !== null);
        var currNode = ref.current;
        resizeObserver.observe(currNode);
        return function () { return resizeObserver.unobserve(currNode); };
    }, [resizeObserver]);
    return ref;
}
function QueryNode(_a) {
    var e_1, _b;
    var _c;
    var data = _a.data, id = _a.id;
    var bodyRef = useResizeObservedRef(data.resizeObserver);
    var headRef = useResizeObservedRef(data.resizeObserver);
    var expanded = useGraphRenderingStore(function (s) { return s.expandedNodes[id]; });
    var toggleNode = useGraphRenderingStore(function (s) { return s.toggleExpandedNode; });
    var subtreeExpanded = useGraphRenderingStore(function (s) { return s.expandedSubtrees[id]; });
    var toggleSubtree = useGraphRenderingStore(function (s) { return s.toggleExpandedSubtree; });
    var hasProperties = (_c = data.properties) === null || _c === void 0 ? void 0 : _c.size;
    var hasSubtree = data.collapsedChildren && data.collapsedChildren.length > 0;
    var onClick = useCallback(function (e) {
        if (e.shiftKey) {
            if (hasSubtree)
                toggleSubtree(id);
        }
        else {
            if (hasProperties)
                toggleNode(id);
        }
        e.stopPropagation();
    }, [toggleNode, toggleSubtree, hasProperties, hasSubtree, id]);
    var onSubtreeHandleClick = useCallback(function (e) {
        if (hasSubtree)
            toggleSubtree(id);
        e.stopPropagation();
    }, [toggleSubtree, hasSubtree, id]);
    var children = [];
    try {
        for (var _d = __values((data.properties || []).entries()), _e = _d.next(); !_e.done; _e = _d.next()) {
            var _f = __read(_e.value, 2), key = _f[0], value = _f[1];
            children.push(_jsxs("div", { children: [_jsxs("span", { className: "qg-prop-name", children: [key, ":"] }), " ", _jsx("span", { className: "qg-prop-value", children: value })] }, key));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_b = _d.return)) _b.call(_d);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var nodeClassName = cc([
        "qg-graph-node",
        {
            "qg-expanded": expanded,
            "qg-collapsed": hasProperties && !expanded,
            "qg-no-props": !hasProperties,
        },
    ]);
    var handleClassName = cc({
        "qg-subtree-handle": hasSubtree,
        "qg-expanded": hasSubtree && subtreeExpanded,
        "qg-collapsed": hasSubtree && !subtreeExpanded,
    });
    return (_jsxs(_Fragment, { children: [_jsx(Handle, { type: "target", position: Position.Top }), _jsxs("div", { className: nodeClassName, onClick: onClick, children: [_jsxs("div", { className: "qg-graph-node-head", ref: headRef, children: [_jsx(NodeIcon, { icon: data.icon, iconColor: data.iconColor }), _jsx("div", { className: "qg-graph-node-label", style: { background: data.nodeColor }, children: data.name })] }), _jsx("div", { className: "qg-graph-node-body-wrapper nowheel", children: _jsx("div", { ref: bodyRef, className: "qg-graph-node-body", children: children }) })] }), _jsx(Handle, { type: "source", position: Position.Bottom, className: handleClassName, onClick: onSubtreeHandleClick, children: hasSubtree ? (subtreeExpanded ? "-" : "+") : "" })] }));
}
var memoizedQueryNode = memo(QueryNode);
export { memoizedQueryNode as QueryNode };
